// src/pages/Login.js
// Sign in page with email and password.
// Includes a forgot password flow that sends a reset email via Shopify.
// On successful login, redirects to /account.

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';

const Login = () => {
    usePageTitle('Sign In', 'Sign in to your MAISON D\'OUD account to view orders and manage your details.');

    const { login, sendPasswordReset, authLoading } = useAuth();
    const navigate = useNavigate();

    // Login form fields
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');

    // Forgot password mode replaces the login form
    const [forgotMode, setForgotMode] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    // Update a single login field and clear any error
    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
    };

    // Submit login credentials to Shopify via AuthContext
    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = await login(formData.email, formData.password);
        if (result.error) {
            setError(result.error);
        } else {
            navigate('/account');
        }
    };

    // Send password reset email via Shopify
    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');
        setResetLoading(true);
        const result = await sendPasswordReset(resetEmail);
        setResetLoading(false);
        if (result.error) {
            setError(result.error);
        } else {
            setResetSent(true);
        }
    };

    // ── Forgot Password View ──────────────────────────────────────────────────
    if (forgotMode) {
        return (
            <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300 flex items-center justify-center">
                <div className="w-full max-w-md">
                    <div className="text-center mb-10">
                        <h1 className="text-4xl font-light text-foreground tracking-wide mb-2">Reset Password</h1>
                        <p className="text-muted-foreground text-sm">Enter your email and we'll send you a reset link.</p>
                    </div>

                    <div className="bg-card rounded-2xl p-8 border border-border">
                        {resetSent ? (
                            /* Confirmation shown after reset email is sent */
                            <div className="text-center py-4">
                                <p className="text-foreground font-light text-lg mb-2">Check your inbox.</p>
                                <p className="text-muted-foreground text-sm mb-6">
                                    We've sent a password reset link to <span className="text-[#CEAC8F]">{resetEmail}</span>.
                                </p>
                                <button
                                    onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(''); setError(''); }}
                                    className="text-[#CEAC8F] text-sm hover:underline"
                                >
                                    Back to Sign In
                                </button>
                            </div>
                        ) : (
                            <>
                                {error && (
                                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                        <p className="text-red-400 text-sm">{error}</p>
                                    </div>
                                )}
                                <form onSubmit={handlePasswordReset} className="space-y-6">
                                    <div>
                                        <label className="block text-muted-foreground text-sm mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            value={resetEmail}
                                            onChange={e => { setResetEmail(e.target.value); setError(''); }}
                                            required
                                            className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={resetLoading}
                                        className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
                                    >
                                        {resetLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </form>
                                <p className="text-center text-muted-foreground text-sm mt-6">
                                    Remember it?{' '}
                                    <button onClick={() => { setForgotMode(false); setError(''); }} className="text-[#CEAC8F] hover:underline">
                                        Back to Sign In
                                    </button>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Login View ────────────────────────────────────────────────────────────
    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300 flex items-center justify-center">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-light text-foreground tracking-wide mb-2">Welcome Back</h1>
                    <p className="text-muted-foreground text-sm">Sign in to your MAISON D'OUD account</p>
                </div>

                <div className="bg-card rounded-2xl p-8 border border-border">

                    {/* Error message from failed login attempt */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-muted-foreground text-sm mb-2">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                placeholder="your@email.com"
                            />
                        </div>

                        <div>
                            {/* Password label with forgot password link */}
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-muted-foreground text-sm">Password</label>
                                <button
                                    type="button"
                                    onClick={() => { setForgotMode(true); setError(''); }}
                                    className="text-[#CEAC8F] text-xs hover:underline"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={authLoading}
                            className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
                        >
                            {authLoading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>

                    {/* Link to registration */}
                    <p className="text-center text-muted-foreground text-sm mt-6">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-[#CEAC8F] hover:underline">
                            Create one
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;